import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getLscPaidBetaSupportStatus } from '../api';
import type { LscPaidBetaSupportStatus } from '../types';
import LoadingState from './LoadingState';

export interface PaidBetaSupportCardProps {
  leagueId: string;
  defaultCollapsed?: boolean;
}

function formatRunTimestamp(value?: string | null): string | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleString();
}

const PaidBetaSupportCard: React.FC<PaidBetaSupportCardProps> = ({
  leagueId,
  defaultCollapsed = true,
}) => {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const [status, setStatus] = useState<LscPaidBetaSupportStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!expanded || loaded) return;
    let cancelled = false;
    setLoading(true);
    void getLscPaidBetaSupportStatus(leagueId)
      .then((data) => {
        if (!cancelled) {
          setStatus(data);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, leagueId, loaded]);

  const lastAutomatedCheck = formatRunTimestamp(
    status?.automation.vod_sync.latest_scheduler_run?.completed_at ??
      status?.automation.vod_sync.latest_scheduler_run?.started_at
  );

  return (
    <section
      className="rounded-xl border border-secondary-800 bg-secondary-950/40"
      data-testid="paid-beta-support-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-800 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-secondary-300">Setup health</h3>
          <p className="text-xs text-secondary-600 mt-0.5">
            League setup summary for the League Owner.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-2 text-sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          data-testid="paid-beta-support-toggle"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4" aria-hidden />
              Hide
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" aria-hidden />
              Show
            </>
          )}
        </button>
      </div>

      {expanded ? (
        <div className="p-4 space-y-4">
          {loading ? <LoadingState message="Loading setup health…" /> : null}
          {!loading && status ? (
            <>
              <dl className="grid gap-3 sm:grid-cols-2 text-xs">
                <div>
                  <dt className="text-secondary-500">Subscription</dt>
                  <dd className="text-secondary-200 mt-0.5">
                    {status.billing.bound
                      ? `${status.billing.plan_display_name ?? status.billing.plan_key ?? 'Founder Beta'} · ${status.billing.subscription_status ?? 'active'}`
                      : 'Not linked'}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Live game detection</dt>
                  <dd className="text-secondary-200 mt-0.5">
                    {status.automation.live_truth.eligible ? 'Ready · preview mode' : 'Needs setup'}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">VOD Sync</dt>
                  <dd className="text-secondary-200 mt-0.5">
                    {status.automation.vod_sync.eligible ? 'Ready · preview mode' : 'Needs setup'}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Archive prep status</dt>
                  <dd className="text-secondary-200 mt-0.5">
                    {status.automation.vod_handoff.apply_enabled ? 'Enabled' : 'Disabled'}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Review counts</dt>
                  <dd className="text-secondary-200 mt-0.5">
                    {status.counts.pending_review} pending · {status.counts.synced_unmatched} unmatched
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Last automated check</dt>
                  <dd className="text-secondary-200 mt-0.5">{lastAutomatedCheck ?? 'No recent run'}</dd>
                </div>
              </dl>
              <p className="text-xs text-secondary-500">
                {status.automation.vod_sync.guardrail_summary ??
                  'VOD Sync preview mode is active. Automatic updates remain disabled.'}
              </p>
            </>
          ) : null}
          {!loading && !status ? (
            <p className="text-xs text-secondary-500">Setup health is unavailable right now.</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default PaidBetaSupportCard;
